import { useEffect, useState } from 'react';
import { addDays, type DateKey } from '@/domain/date';
import type { PrayerId } from '@/domain/prayer';
import { RANGES } from '@/domain/report';
import {
  computeStreaks,
  historyReach,
  runsToOldest,
  summarisePrayers,
  type DayRecord,
  type PrayerConsistency,
  type StreakSummary,
} from '@/domain/streaks';
import { currentPrayer } from '@bonyaan/prayer-times';
import type { RecordsPort } from '@/ports/records';
import { useAppState } from './useAppState';

const WINDOW = 60;
const MAX_DAYS = 366 * 5;

const NO_STREAKS: StreakSummary = { prayed: 0, onTime: 0, jamaah: 0, takbirah: 0 };

export interface LoadedHistory {
  /** Oldest first, one entry per day, empty days included. */
  days: DayRecord[];
  streaks: StreakSummary;
}

export interface HistoryOptions {
  /**
   * Load at least this far back whatever the runs need. The statistics are
   * read over a range the reader chooses, and a widening walk that stopped as
   * soon as the runs were settled could return a fortnight to a section
   * drawing ninety days.
   */
  minDays?: number;
  /**
   * Keep widening while the oldest day loaded is still part of a run. A
   * personal best can lie anywhere in the history rather than at its near
   * end, so nothing short of a blank day settles one.
   */
  bests?: boolean;
}

/**
 * Has the history got everything asked of it?
 *
 * Three questions, and the walk stops only when all three are answered. The
 * cheap ones come first: neither of the other two is worth a walk over the
 * days while the depth is still short of the minimum.
 */
function settled(
  days: DayRecord[],
  today: DateKey,
  current: PrayerId,
  loaded: number,
  { minDays = 0, bests = false }: HistoryOptions,
): boolean {
  if (loaded < minDays) return false;
  // `loaded - 1`, not `loaded`: a run that starts at yesterday (because today
  // is not complete yet) reaches the oldest loaded day one short of the
  // window's length, and stopping at `loaded` reported a 100-day run as 59.
  if (historyReach(days, today, current) >= loaded - 1) return false;
  return !bests || !runsToOldest(days);
}

/**
 * Streaks are open-ended, so the history is pulled in windows rather than
 * guessed at: keep widening while any run still reaches the oldest day loaded,
 * since that is the only case where the answer could still grow. Bounded so a
 * corrupt record cannot spin.
 *
 * The days come back with the streaks because everything else on top of this —
 * the per-prayer runs and rates — is computed from the same history, and a
 * second widening walk to fetch it again would be the same reads twice.
 */
export async function loadHistory(
  records: RecordsPort,
  today: DateKey,
  current: PrayerId,
  options: HistoryOptions = {},
): Promise<LoadedHistory> {
  let days: DayRecord[] = [];
  let loaded = 0;

  while (loaded < MAX_DAYS) {
    const to = addDays(today, -loaded);
    const from = addDays(to, -(WINDOW - 1));
    days = [...(await records.range(from, to)), ...days];
    loaded += WINDOW;

    if (settled(days, today, current, loaded, options)) break;
  }

  return { days, streaks: computeStreaks(days, today, current) };
}

export async function loadStreaks(
  records: RecordsPort,
  today: DateKey,
  current: PrayerId,
): Promise<StreakSummary> {
  return (await loadHistory(records, today, current)).streaks;
}

/**
 * The prayer whose time it is, which is where every run is counted back from.
 * A string, so the reload below fires at each adhān rather than every minute
 * the clock ticks.
 */
function useCurrentPrayer(): PrayerId {
  const { times, now } = useAppState();
  return currentPrayer(times, now);
}

export function useStreaks(today: DateKey, version: unknown): StreakSummary {
  const { backend } = useAppState();
  const current = useCurrentPrayer();
  const [streaks, setStreaks] = useState<StreakSummary>(NO_STREAKS);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await loadStreaks(backend.records, today, current);
      if (!cancelled) setStreaks(result);
    })();
    return () => {
      cancelled = true;
    };
  }, [backend, today, current, version]);

  return streaks;
}

export interface ReportHistory {
  /** Oldest first, one entry per day — what the statistics are counted over. */
  days: DayRecord[];
  /** Each prayer's own run and record, against each of the four. */
  prayers: PrayerConsistency[];
}

/**
 * Everything the report reads about now, in one widening walk.
 *
 * Deeper than the home screen's load, in both of the directions the report
 * asks for: at least the longest range the statistics can be read over, and
 * as far back as a personal best can reach. The days come back alongside the
 * runs because the statistics are counted over the same history — fetching it
 * twice would be the same reads twice.
 */
export function useReportStats(today: DateKey, version: unknown): ReportHistory | null {
  const { backend } = useAppState();
  const current = useCurrentPrayer();
  const [stats, setStats] = useState<ReportHistory | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { days } = await loadHistory(backend.records, today, current, {
        minDays: Math.max(...RANGES),
        bests: true,
      });
      if (!cancelled) setStats({ days, prayers: summarisePrayers(days, today, current) });
    })();
    return () => {
      cancelled = true;
    };
  }, [backend, today, current, version]);

  return stats;
}
